/**
 * Per-section resume optimization.
 *
 * Each section gets its own focused Groq call via the `section_optimize` handler
 * in the Supabase `optimize-resume` edge function. The Groq API key lives server-side.
 * Calls run as guest (no auth header) → Groq free tier, no DB write, no credits.
 */

export interface SectionInput {
  title: string;
  content: string;
}

export interface JobContext {
  title?: string;
  company?: string;
  description?: string;
  skills?: string[];
  requirements?: string[];
}

/** Optional context so the LLM can produce unique, candidate-specific output. */
export interface ResumeContext {
  candidateName?: string;
  /** Short summary of the rest of the resume (other section titles or first N chars) to avoid generic phrasing. */
  fullResumeSummary?: string;
}

export interface SectionResult {
  title: string;
  originalContent: string;
  optimizedContent: string;
  skipped?: boolean;
}

// Only these sections benefit from LLM rewrites; others are formatted locally
const LLM_SECTIONS = new Set([
  'professional summary', 'summary', 'objective', 'career summary',
  'work experience', 'experience', 'professional experience', 'employment history', 'career history',
  'skills', 'technical skills', 'key skills', 'core competencies', 'competencies',
  'projects', 'key projects',
]);

function needsLLM(title: string): boolean {
  return LLM_SECTIONS.has(title.toLowerCase().trim());
}

function localFormatSection(section: SectionInput): string {
  return section.content
    .split('\n')
    .map(l => {
      const t = l.trim();
      if (!t) return '';
      if (t.startsWith('- ') || t.startsWith('* ')) return '• ' + t.slice(2);
      return t;
    })
    .join('\n')
    .trim();
}

export async function optimizeSection(
  section: SectionInput,
  job: JobContext,
  signal?: AbortSignal,
  resumeContext?: ResumeContext,
): Promise<SectionResult> {
  if (!needsLLM(section.title)) {
    return {
      title: section.title,
      originalContent: section.content,
      optimizedContent: localFormatSection(section),
      skipped: true,
    };
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    return { title: section.title, originalContent: section.content, optimizedContent: section.content, skipped: true };
  }

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/optimize-resume`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        // No Authorization header → guest mode → Groq (free), no DB write
      },
      body: JSON.stringify({
        useCase: 'section_optimize',
        model: 'groq-llama-3.3-70b-versatile',
        sectionTitle: section.title,
        sectionContent: section.content,
        jobData: {
          title: job.title || '',
          company: job.company || '',
          description: job.description || '',
          skills: job.skills || [],
          requirements: job.requirements || [],
        },
        resumeContext: resumeContext
          ? {
              candidateName: resumeContext.candidateName || undefined,
              fullResumeSummary: resumeContext.fullResumeSummary || undefined,
            }
          : undefined,
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Edge function ${response.status}: ${errText.slice(0, 200)}`);
    }

    const data = await response.json();
    const optimized = (data.optimizedContent || '').trim();

    return {
      title: section.title,
      originalContent: section.content,
      optimizedContent: optimized.length > 20 ? optimized : section.content,
    };
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    console.error(`[SectionOptimize] Failed for "${section.title}":`, err);
    return { title: section.title, originalContent: section.content, optimizedContent: section.content, skipped: true };
  }
}
